import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "../../layout/Layout";
import PageBanner from "../../layout/PageBanner";
import { paymentService, QR_EXPIRE_SECONDS, QR_POLL_MS, QR_INQUIRY_MS } from "../../services";
import { Panel, Skeleton, money } from "../../components/ui";

const SUCCESS_STATUS = ["success", "settled", "completed"];
const FAILED_STATUS = ["failed", "expired", "cancelled", "reject", "rejected"];

const formatClock = (seconds) => {
  const safe = Math.max(0, seconds);
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
};

export default function QrPayment() {
  const { transactionId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [qr, setQr] = useState(state?.base64 || "");
  const [amount, setAmount] = useState(state?.amount || 0);
  const [status, setStatus] = useState("pending");
  const [secondsLeft, setSecondsLeft] = useState(QR_EXPIRE_SECONDS);
  const [error, setError] = useState("");
  const expiredAtRef = useRef(state?.expireDatetime ? new Date(state.expireDatetime).getTime() : null);

  // อ่านสถานะล่าสุดของ transaction
  const refresh = useCallback(async () => {
    const data = await paymentService.getTransaction(transactionId);
    if (!data) return null;

    if (data.base64) setQr(data.base64);
    if (data.amount) setAmount(data.amount);
    if (data.expiredAt) expiredAtRef.current = new Date(data.expiredAt).getTime();
    setStatus(data.status);
    return data.status;
  }, [transactionId]);

  useEffect(() => {
    let alive = true;
    refresh().catch(() => alive && setError("โหลดข้อมูลรายการไม่สำเร็จ"));
    return () => {
      alive = false;
    };
  }, [refresh]);

  // นับถอยหลังอายุ QR
  useEffect(() => {
    const timer = setInterval(() => {
      const expiredAt = expiredAtRef.current;
      if (expiredAt) setSecondsLeft(Math.round((expiredAt - Date.now()) / 1000));
      else setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // poll สถานะจนกว่าจะจ่ายสำเร็จ/ล้มเหลว
  useEffect(() => {
    if (SUCCESS_STATUS.includes(status) || FAILED_STATUS.includes(status)) return undefined;

    const pollTimer = setInterval(() => {
      refresh().catch(() => {});
    }, QR_POLL_MS);

    // ยิง inquiry ไปถาม gateway ต้นทางเป็นระยะ เผื่อ callback มาไม่ถึง
    const inquiryTimer = setInterval(() => {
      paymentService.inquiry(transactionId).catch(() => {});
    }, QR_INQUIRY_MS);

    return () => {
      clearInterval(pollTimer);
      clearInterval(inquiryTimer);
    };
  }, [status, refresh, transactionId]);

  // จ่ายสำเร็จ -> ไปหน้าสรุป
  useEffect(() => {
    if (!SUCCESS_STATUS.includes(status)) return;
    navigate("/order/success", {
      replace: true,
      state: {
        orderCode: state?.refNo || transactionId,
        title: state?.title || "ชำระเงินสำเร็จ",
        detail: state?.detail,
        total: amount,
        historyPath: state?.historyPath || "/profile/topup-game-history",
      },
    });
  }, [status, navigate, state, amount, transactionId]);

  const downloadQr = () => {
    if (!qr) return;
    const link = document.createElement("a");
    link.href = qr;
    link.download = `lucidtopup_qr_${transactionId}.png`;
    link.click();
  };

  const isExpired = FAILED_STATUS.includes(status) || secondsLeft <= 0;

  return (
    <Layout>
      <PageBanner title="ชำระเงินด้วย QR" breadcrumb={[{ label: "ชำระเงิน" }]} seed={19} />

      <div className="lucid-container" style={{ marginTop: -30, position: "relative", zIndex: 3, paddingBottom: 40 }}>
        <Panel style={{ maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
          <h2 className="lt-panel__title">{state?.title || "สแกนเพื่อชำระเงิน"}</h2>
          {state?.detail ? <p className="lt-text-muted lt-mb-0">{state.detail}</p> : null}

          <div style={{ fontSize: 28, fontWeight: 900, margin: "12px 0" }}>฿ {money(amount)}</div>

          {error ? <p className="lt-helper lt-helper--danger">{error}</p> : null}

          {!qr ? (
            <Skeleton height={280} />
          ) : (
            <div
              style={{
                background: "#fff",
                border: "1px solid var(--lt-line)",
                borderRadius: 16,
                padding: 16,
                maxWidth: 320,
                margin: "0 auto",
                opacity: isExpired ? 0.35 : 1,
              }}
            >
              <img src={qr} alt="QR Code" style={{ width: "100%", display: "block" }} />
            </div>
          )}

          {isExpired ? (
            <p className="lt-helper lt-helper--danger lt-mt-24">
              {FAILED_STATUS.includes(status) ? "รายการนี้ถูกยกเลิกหรือหมดอายุแล้ว" : "QR หมดอายุแล้ว กรุณาทำรายการใหม่"}
            </p>
          ) : (
            <div
              className="lt-mt-24"
              style={{ background: "#fdf8ff", border: "1px solid var(--lt-purple-100)", borderRadius: 12, padding: 14 }}
            >
              <div className="lt-helper">ห้ามปิดหน้านี้จนกว่าจะชำระเงินสำเร็จ · QR จะหมดอายุในอีก</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "var(--lt-purple-600)" }}>{formatClock(secondsLeft)}</div>
              <div className="lt-helper" style={{ marginTop: 6 }}>
                ระบบกำลังตรวจสอบการชำระเงินอัตโนมัติ...
              </div>
            </div>
          )}

          <div className="lt-flex lt-gap-12 lt-mt-24">
            <button type="button" className="lt-btn lt-btn--ghost" style={{ flex: 1 }} onClick={downloadQr} disabled={!qr}>
              ดาวน์โหลด QR
            </button>
            <Link to={state?.historyPath || "/profile/topup-game-history"} className="lt-btn lt-btn--primary" style={{ flex: 1 }}>
              ดูประวัติคำสั่งซื้อ
            </Link>
          </div>

          <p className="lt-helper lt-mt-24">เลขที่รายการ: {state?.refNo || transactionId}</p>
        </Panel>
      </div>
    </Layout>
  );
}
